import * as React from 'react';

import { Box, HStack, Icon, IconButton, Separator, Text } from '@chakra-ui/react';
import { ArrowBack, Menu } from '@m/icons';

export interface SubpageHeaderProps {
  /** Handler for back button click */
  onBackClick?: () => void;
  /** Right padding on the header row. Defaults to 4 (16px). */
  pr?: string | number;
  /** Optional right-side content for future extensibility */
  rightAction?: React.ReactNode;
  /** Bottom divider under the header. Defaults to true. */
  showBorder?: boolean;
  /** Show menu icon instead of back arrow */
  showMenuIcon?: boolean;
  /** Show the page title. Defaults to true. */
  showTitle?: boolean;
  /** The page title text */
  title?: string;
}

export const SubpageHeader = ({
  title,
  onBackClick,
  pr = 4,
  rightAction,
  showMenuIcon = false,
  showBorder = true,
  showTitle = true,
}: SubpageHeaderProps) => {
  const IconComponent = showMenuIcon ? Menu : ArrowBack;
  const ariaLabel = showMenuIcon ? 'Open menu' : 'Go back';

  return (
    <Box bg="fill.background.default" width="100%" flexShrink={0}>
      <HStack gap={2} align="center" width="100%" height="56px" pr={pr}>
        {/* Left: Back button or Menu button - 56x56px clickable area */}
        {!onBackClick ? (
          <Box flexShrink={0} width="56px" height="56px" />
        ) : (
          <IconButton
            variant="ghost"
            onClick={onBackClick}
            aria-label={ariaLabel}
            flexShrink={0}
            width="56px"
            height="56px"
            disabled={!onBackClick}
          >
            <Icon as={IconComponent} boxSize={8} />
          </IconButton>
        )}

        <Box alignItems="center" display="flex" flexGrow={1} justifyContent="center" minW={0}>
          {showTitle && (
            <Text variant="MainBodyDemiBold" textAlign="center" color="text.primary" flexGrow={1}>
              {title}
            </Text>
          )}
        </Box>

        {/* Right: min 56px to balance the back control; widen for pill actions (e.g. Help). */}
        <Box
          flexShrink={0}
          minW="56px"
          w={rightAction ? 'auto' : '56px'}
          height="56px"
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
        >
          {rightAction}
        </Box>
      </HStack>

      {showBorder && <Separator borderColor="stroke.divider.emphasized" />}
    </Box>
  );
};

export default SubpageHeader;
